import { useContext, useEffect, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import SuccessModal from "./SuccessModal";
import { useNotificationModal } from "../../hooks/useNotificationModal";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3000";

export default function AddEditLocationModal({
  onSuccess,
  editData = null,
  plantId, // Plant ID or Warehouse ID passed from parent component
  plantData = null, // Plant or Warehouse data passed from parent component
  isWarehouse = false, // Flag to indicate if this is for warehouse
} = {}) {
  const { userData } = useContext(AuthContext);
  const isUpdateMode = Boolean(editData);

  const {
    showSuccessModal,
    successMessage,
    successTitle,
    showErrorModal,
    errorMessage,
    errorTitle,
    showSuccess,
    hideSuccess,
    hideError,
  } = useNotificationModal();

  const [formData, setFormData] = useState({
    location_name: "",
    location_code: "",
    description: "",
  });
  const [errorMsg, setErrorMsg] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [, setPlantLoading] = useState(false);
  const [autoGeneratedCode, setAutoGeneratedCode] = useState("");
  const [, setExistingLocations] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Set plant data from parent and fetch existing locations
  useEffect(() => {
    const initializeData = async () => {
      if (!plantId || !userData?.company_code || !plantData) {
        setDataLoaded(true);
        return;
      }

      setPlantLoading(true);
      setDataLoaded(false);
      try {
        // Fetch existing locations for this plant
        const locationsResponse = await axios.get(
          `${BASE_URL}/api/admin/sites/get-all-locations-by-plant-id`,
          {
            params: {
              company_code: userData.company_code,
              plant_id: plantId,
            },
          }
        );
        if (locationsResponse.data.success) {
          const locations = locationsResponse.data.items || [];
          setExistingLocations(locations);
        }

        setDataLoaded(true);
      } catch (error) {
        console.error("Error fetching data:", error);
        setDataLoaded(true);
      } finally {
        setPlantLoading(false);
      }
    };

    initializeData();
  }, [plantId, userData?.company_code, plantData]);

  // Generate location code based on plant site code and location name
  const generateLocationCode = (locationName) => {
    if (!plantData) {
      return "";
    }

    if (!locationName) {
      return "";
    }

    if (!plantData.site_code) {
      console.warn("🔍 Plant site_code not available");
      return "";
    }

    // Get plant site code
    const plantSiteCode = plantData.site_code;

    // Generate code with plant site code and location name
    const generatedCode = `${plantSiteCode}-${locationName}`;
    return generatedCode;
  };

  // Display existing data in edit mode
  useEffect(() => {
    if (editData) {
      setFormData({
        location_name: editData.location_name || "",
        location_code: editData.location_code || "",
        description: editData.description || "",
      });
    }
  }, [editData]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "location_name" && !isUpdateMode && dataLoaded) {
      // Generate code when location name changes
      const generatedCode = generateLocationCode(value);
      setAutoGeneratedCode(generatedCode);

      setFormData((prev) => ({
        ...prev,
        [name]: value,
        location_code: generatedCode,
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleResetCode = () => {
    setFormData((prev) => ({ ...prev, location_code: autoGeneratedCode }));
  };

  const createLocation = async () => {
    try {
      // Validate location code
      if (!formData.location_code || formData.location_code.trim() === "") {
        setErrorMsg("Location code is required");
        setTimeout(() => setErrorMsg(""), 3000);
        return;
      }

      const requestBody = {
        location_name: formData.location_name,
        location_code: formData.location_code,
        description: formData.description,
      };

      // For warehouse, use site_id; for plant, use plant_id
      if (isWarehouse) {
        requestBody.site_id = plantId;
      } else {
        requestBody.plant_id = plantId;
      }

      const response = await axios.post(
        `${BASE_URL}/api/admin/locations/add-location`,
        requestBody,
        {
          params: {
            company_code: userData.company_code,
            site_id: plantId,
          },
        }
      );

      if (response.data.success) {
        showSuccess(
          `Location created successfully! Location Code: ${formData.location_code}`,
          "Success"
        );
        setTimeout(() => {
          hideSuccess();
          if (onSuccess) onSuccess();
        }, 2000);
      } else {
        throw new Error(response.data.message || "Failed to create location");
      }
    } catch (error) {
      throw error;
    }
  };

  const updateLocation = async () => {
    try {
      const requestBody = {
        location_name: formData.location_name,
        location_code: formData.location_code,
        description: formData.description,
      };

      // For warehouse, use site_id; for plant, use plant_id
      if (isWarehouse) {
        requestBody.site_id = plantId;
      } else {
        requestBody.plant_id = plantId;
      }

      const response = await axios.put(
        `${BASE_URL}/api/admin/locations/update-location/${editData.location_id}`,
        requestBody,
        {
          params: {
            company_code: userData.company_code,
            type: "location",
          },
        }
      );

      if (response.data.success) {
        showSuccess("Location updated successfully!", "Success");
        setTimeout(() => {
          hideSuccess();
          if (onSuccess) onSuccess();
        }, 2000);
      } else {
        throw new Error(response.data.message || "Failed to update location");
      }
    } catch (error) {
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMsg("");

    try {
      if (isUpdateMode) {
        await updateLocation();
      } else {
        await createLocation();
      }
    } catch (error) {
      console.error(`${isUpdateMode ? "Update" : "Create"} error:`, error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong. Please try again.";
      setErrorMsg(errorMessage);
      setTimeout(() => setErrorMsg(""), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <SuccessModal
        show={showSuccessModal}
        onHide={hideSuccess}
        title={successTitle}
        message={successMessage}
        variant="success"
      />
      <SuccessModal
        show={showErrorModal}
        onHide={hideError}
        title={errorTitle}
        message={errorMessage}
        variant="error"
      />
      {errorMsg && (
        <Alert variant="danger" className="text-center">
          {errorMsg}
        </Alert>
      )}

      <Form className="register-form" onSubmit={handleSubmit}>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Location Name*</Form.Label>
              <Form.Control
                type="text"
                name="location_name"
                value={formData.location_name}
                onChange={handleChange}
                placeholder="Enter location name"
                required
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Location Code*</Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  name="location_code"
                  value={formData.location_code}
                  onChange={handleChange}
                  placeholder="Enter location code"
                  required
                />
                {!isUpdateMode &&
                  autoGeneratedCode &&
                  formData.location_code !== autoGeneratedCode && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleResetCode}
                      title="Reset to auto-generated code"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      Reset
                    </Button>
                  )}
              </div>
              {isUpdateMode ? (
                <Form.Text className="text-muted">
                  Location code cannot be changed
                </Form.Text>
              ) : (
                <Form.Text className="text-muted">
                  {!dataLoaded
                    ? "Loading..."
                    : !plantData
                    ? "Plant data not available"
                    : autoGeneratedCode
                    ? `Auto-generated: ${autoGeneratedCode}`
                    : "Enter location name to generate code"}
                </Form.Text>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="Additional description about the location"
              />
            </Form.Group>
          </Col>
        </Row>

        <div className="text-center">
          <Button type="submit" className="register-btn" disabled={isLoading}>
            {isLoading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                {isUpdateMode ? "Updating Location..." : "Creating Location..."}
              </>
            ) : isUpdateMode ? (
              "Update Location"
            ) : (
              "Create Location"
            )}
          </Button>
        </div>
      </Form>
    </>
  );
}
